import type { LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';
import type { NavNode } from './types';
import { resolveMenuIcon } from '../icons/menuIcons';

/** A row of GET /api/menu-permissions/navigation (already filtered by access and visibility). */
export interface NavigationEntry {
  menuKey: string;
  label: string;
  icon: string | null;
  navParentKey: string | null;
  sortOrder: number;
}

export interface ResolvedNavItem {
  key: string;
  label: string;
  icon: LucideIcon;
  path: string;
  section?: string;
}

export type ResolvedNavNode =
  | { kind: 'link'; key: string; label: string; icon: LucideIcon; path: string; end?: boolean }
  | {
      kind: 'group';
      key: string;
      label: string;
      icon: LucideIcon;
      header?: ComponentType<{ onSelect?: () => void }>;
      items: ResolvedNavItem[];
    };

const MAX_DEPTH = 3;

/**
 * Build the sidebar from the database entries and the modules' nav nodes.
 *
 * - Top-level entries are rows without navigation parent, in sort order.
 * - An entry shows its module's inner links (NavNode.items, checked with
 *   hasAccess) plus the rows placed under it. A row placed under it that is
 *   a page becomes a link; a row that is itself a group or module adds its
 *   links under a section heading with the row's label.
 * - An entry with a page and nothing inside is a link; a group with exactly
 *   one link and no header becomes a direct link to that page; an empty
 *   group is left out.
 */
export function buildNavigation(
  entries: NavigationEntry[],
  nodes: Map<string, NavNode>,
  hasAccess: (menuKey: string) => boolean,
): ResolvedNavNode[] {
  const sorted = [...entries].sort((a, b) => a.sortOrder - b.sortOrder || a.menuKey.localeCompare(b.menuKey));
  const childrenOf = new Map<string, NavigationEntry[]>();
  for (const entry of sorted) {
    if (!entry.navParentKey) continue;
    const list = childrenOf.get(entry.navParentKey) ?? [];
    list.push(entry);
    childrenOf.set(entry.navParentKey, list);
  }

  const collect = (entry: NavigationEntry, depth: number): ResolvedNavItem[] => {
    const node = nodes.get(entry.menuKey);
    const ownItems: ResolvedNavItem[] = (node?.items ?? [])
      .filter(item => hasAccess(item.menuKey))
      .map(item => ({ key: `${item.menuKey}:${item.path}`, label: item.label, icon: item.icon, path: item.path, section: item.section }));
    const ownKeys = new Set((node?.items ?? []).map(item => item.menuKey));

    const placed: ResolvedNavItem[] = [];
    for (const child of childrenOf.get(entry.menuKey) ?? []) {
      if (ownKeys.has(child.menuKey)) continue;
      const childNode = nodes.get(child.menuKey);
      if (childNode?.path) {
        placed.push({
          key: child.menuKey,
          label: child.label,
          icon: resolveMenuIcon(child.icon, childNode.defaultIcon),
          path: childNode.path,
        });
      } else if (depth < MAX_DEPTH) {
        const inner = collect(child, depth + 1);
        inner.forEach((item, i) => placed.push(i === 0 ? { ...item, section: child.label } : item));
      }
    }
    return [...ownItems, ...placed];
  };

  const result: ResolvedNavNode[] = [];
  for (const entry of sorted) {
    if (entry.navParentKey) continue;
    const node = nodes.get(entry.menuKey);
    const icon = resolveMenuIcon(entry.icon, node?.defaultIcon);
    const items = collect(entry, 1);

    if (node?.path && items.length === 0) {
      result.push({ kind: 'link', key: entry.menuKey, label: entry.label, icon, path: node.path, end: node.path === '/' });
    } else if (items.length === 0) {
      continue;
    } else if (items.length === 1 && !node?.header && !node?.path) {
      result.push({ kind: 'link', key: entry.menuKey, label: entry.label, icon, path: items[0].path });
    } else {
      result.push({ kind: 'group', key: entry.menuKey, label: entry.label, icon, header: node?.header, items });
    }
  }
  return result;
}

/** Does `pathname` belong to the page at `path` (exact or a sub-path)? */
export function matchesPath(pathname: string, path: string): boolean {
  if (path === '/') return pathname === '/';
  const base = path.replace(/\/:[^/]+.*$/, '');
  return pathname === base || pathname.startsWith(`${base}/`);
}
